import {
  ErrorBoundaries,
  EzDrawer,
  EzDrawerProvider,
  GlobalErrorHandler,
  NetworkDetector,
  SplashScreen,
} from '@biznessforce/ui-lib';
import { App as AntdApp, ConfigProvider, message, theme as antdTheme } from 'antd';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { useEffect } from 'react';
import { Provider, connect } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import Routes from './routes/routes';
import { requestUser } from './store/auth/auth.actions';
import { updateGlobalError } from './store/config/config.actions';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

type Props = {
  store: any;
  persistor: any;
  basename?: string;
};

type AppContentProps = {
  basename?: string;
  requestUser: () => any;
  updateGlobalError: (v: any) => any;
  globalError: any;
  ajaxLoader: { isLoading: boolean };
};

function AppContent({
  basename,
  requestUser,
  globalError,
  ajaxLoader,
  updateGlobalError,
}: AppContentProps) {
  const { isLoading } = ajaxLoader || { isLoading: false };
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (isLoading) {
      messageApi.open({ type: 'loading', content: 'Loading...', duration: 0 });
    } else {
      messageApi.destroy();
    }
  }, [isLoading, messageApi]);

  useEffect(() => {
    requestUser();
  }, [requestUser]);

  return (
    <React.Suspense fallback={<SplashScreen />}>
      <BrowserRouter basename={basename}>
        <ConfigProvider
          theme={{
            algorithm: antdTheme.defaultAlgorithm,
            token: {
              colorText: '#12344d',
              colorPrimary: '#12344d',
            },
            components: {
              Button: {
                colorPrimary: '#12344d',
                algorithm: true,
              },
            },
          }}
        >
          <AntdApp>
            {contextHolder}
            <NetworkDetector>
              <GlobalErrorHandler
                globalError={globalError}
                updateGlobalError={(v) => updateGlobalError(v)}
              >
                <ErrorBoundaries>
                  <EzDrawerProvider>
                    <Routes />
                    <EzDrawer />
                  </EzDrawerProvider>
                </ErrorBoundaries>
              </GlobalErrorHandler>
            </NetworkDetector>
          </AntdApp>
        </ConfigProvider>
      </BrowserRouter>
    </React.Suspense>
  );
}

// Connect AppContent to Redux
const ConnectedAppContent = connect(
  (state: any) => ({
    globalError: Object(state.config?.globalError),
    ajaxLoader: Object(state.config?.ajaxLoader || {}),
  }),
  {
    requestUser,
    updateGlobalError,
  }
)(AppContent);

export function App({ store, persistor, basename }: Props) {
  return (
    <Provider store={store}>
      <PersistGate persistor={persistor} loading={<SplashScreen />}>
        <ConnectedAppContent basename={basename} />
      </PersistGate>
    </Provider>
  );
}

export default App;
